import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";
import { FaChartPie } from "react-icons/fa";
import { useDataCache } from "../../contexts/DataCacheContext";
import CollapseToggle from "./CollapseToggle";

const buildChartData = (res) => {
	if (res?.labels?.length) {
		return res.labels.map((label, i) => ({
			name: label,
			value: res.values[i],
			color: res.colors[i],
		}));
	}
	return [];
};

const CustomTooltip = ({ active, payload }) => {
	if (active && payload && payload.length) {
		const entry = payload[0].payload;
		return (
			<div style={{
				backgroundColor: "#1a1a2e",
				padding: "8px 12px",
				borderRadius: 8,
				display: "flex",
				alignItems: "center",
				gap: 8,
				boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
				border: "1px solid rgba(255,255,255,0.08)",
			}}>
				<div style={{ width: 10, height: 10, backgroundColor: entry.color, borderRadius: 2, flexShrink: 0 }} />
				<span style={{ color: "#ffffff", fontSize: 12, fontWeight: 500 }}>
					{entry.name}: {entry.value}
				</span>
			</div>
		);
	}
	return null;
};

const PriorityPieChart = ({ fetchFunc, clientId, forceCollapsed = 0 }) => {
	const { getCachedData, setCachedData } = useDataCache();
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isCollapsed, setIsCollapsed] = useState(false);

	useEffect(() => { if (forceCollapsed > 0) setIsCollapsed(true); }, [forceCollapsed]);

	useEffect(() => {
		if (!fetchFunc || !clientId) return;

		const cacheKey = "ticket_priority_distribution";
		const cached = getCachedData(cacheKey, clientId);

		if (cached !== undefined) {
			setData(buildChartData(cached));
			setLoading(false);
			return;
		}

		setLoading(true);
		fetchFunc(clientId)
			.then((res) => {
				setData(buildChartData(res));
				setCachedData(cacheKey, res, clientId);
				setLoading(false);
			})
			.catch(() => {
				setData([]);
				setLoading(false);
			});
	}, [fetchFunc, clientId, getCachedData, setCachedData]);

	const isDark = typeof document !== "undefined" && document.body.classList.contains("dark-mode");

	// ── Shared card shell ────────────────────────────────────────────────────
	// flex/minHeight are gated on isCollapsed so the card can shrink to its
	// header on its own, without the parent page needing to know anything
	// about collapse state: the Col/InViewFadeUp wrappers above this still
	// get stretched to match the sibling table by the row's
	// align-items-stretch, but they're just transparent flex containers —
	// once this element stops trying to grow/fill them ("0 0 auto" instead
	// of flex: 1), the visible card just sizes to its own content again.
	const cardShell = (content) => (
		<motion.div
			initial={{ opacity: 0, y: 25 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
			style={{ flex: isCollapsed ? "0 0 auto" : 1, display: "flex", flexDirection: "column" }}
		>
			{/* Outer card: flex-col that grows to match the sibling table height (when expanded) */}
			<div style={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				flex: isCollapsed ? "0 0 auto" : 1,
				minHeight: isCollapsed ? 0 : 320,
				padding: 24,
				borderRadius: 16,
				background: "var(--color-bg-card)",
				border: "1px solid var(--color-border)",
				boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
			}}>
				{/* Header */}
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
					<h6 className="fw-bold m-0 d-flex align-items-center gap-2" style={{ color: "var(--color-text)" }}>
						<div style={{ width: 32, height: 32, borderRadius: 10, background: "rgba(111,66,193,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
							<FaChartPie size={16} color="#6f42c1" />
						</div>
						Ticket Priority Distribution (90 Days)
					</h6>
					<CollapseToggle collapsed={isCollapsed} onToggle={() => setIsCollapsed((p) => !p)} />
				</div>

				{/* Collapsible body — maxHeight animation avoids Framer setting height:auto,
				    which would block flex-grow from filling the card to the sibling's height */}
				<motion.div
					initial={false}
					animate={{ maxHeight: isCollapsed ? 0 : 1200, opacity: isCollapsed ? 0 : 1 }}
					transition={{ duration: 0.3, ease: "easeInOut" }}
					style={{ overflow: "hidden", flexGrow: 1, display: "flex", flexDirection: "column" }}
				>
					{content}
				</motion.div>
			</div>
		</motion.div>
	);

	// ── Loading ──────────────────────────────────────────────────────────────
	if (loading || !data) {
		return cardShell(
			<>
				<div style={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
					<div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(139,92,246,0.3)", borderTopColor: "#8b5cf6", animation: "spin 0.8s linear infinite" }} />
					<span style={{ color: "var(--color-text-light)", fontSize: 13 }}>Loading...</span>
				</div>
				<style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
			</>
		);
	}

	// ── Empty ────────────────────────────────────────────────────────────────
	if (data.length === 0) {
		return cardShell(
			<div style={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
				<p style={{ color: "var(--color-text-light)" }}>No data available</p>
			</div>
		);
	}

	const total = data.reduce((sum, d) => sum + d.value, 0);

	return cardShell(
		<>
			<style>{`.priority-pie-wrapper *:focus, .priority-pie-wrapper svg { outline: none !important; }`}</style>

			{/*
			 * Middle chart layout wrapper — flex-centered, grows to fill card body.
			 * The chart canvas itself gets a fixed pixel height so ResponsiveContainer
			 * can measure it reliably (it cannot resolve height from a flex-grow parent).
			 */}
			<div style={{
				position: "relative",
				width: "100%",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				flexGrow: 1,
			}}>
				{/* Fixed-height block that ResponsiveContainer can measure */}
				<div className="priority-pie-wrapper" style={{ width: "100%", height: 280 }}>
					<ResponsiveContainer width="100%" height="100%">
						<PieChart>
							<Pie
								data={data}
								dataKey="value"
								nameKey="name"
								cx="50%"
								cy="50%"
								startAngle={360}
								endAngle={0}
								innerRadius="52%"
								outerRadius="80%"
								paddingAngle={2}
								stroke="none"
								animationDuration={1200}
								animationEasing="ease-out"
							>
								{data.map((entry, index) => (
									<Cell key={`cell-${index}`} fill={entry.color} />
								))}
							</Pie>
							<Tooltip content={<CustomTooltip />} />
						</PieChart>
					</ResponsiveContainer>
				</div>

				{/* Center total — absolutely positioned over the donut hole */}
				<div style={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					pointerEvents: "none",
					zIndex: 10,
				}}>
					<span style={{ fontSize: 24, fontWeight: 700, color: isDark ? "#f1f5f9" : "#1e293b", lineHeight: 1 }}>
						{total.toLocaleString()}
					</span>
					<span style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
						Total Tickets
					</span>
				</div>
			</div>

			{/* Horizontal legend pinned to the bottom of the card */}
			<div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "6px 16px", paddingTop: 12 }}>
				{data.map((entry, index) => (
					<div key={`legend-${index}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
						<div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: entry.color, flexShrink: 0 }} />
						<span style={{ fontSize: 12, fontWeight: 400, color: isDark ? "#a0a4b8" : "#6c737f" }}>
							{entry.name}
						</span>
					</div>
				))}
			</div>
		</>
	);
};

export default PriorityPieChart;
